import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { toast } from 'react-toastify';
import XLSX from 'sheetjs-style';
import Layout from './DashboardLayout';
import Loading from '../components/Loader';
import { ImportOvertime, DeleteOvertimeSlot } from '../components/scheduling';
import { schedulesService, projectService } from '../services';
import {
  ProjectListDropdown,
  MultiSelectItemDropdown,
} from '../components/common';
import { formatTimeOption } from '../helpers';
import { PAGINATION_LIMIT, NO_DATA, OVERTIME_SLOTS } from '../constants';
import Pagination from '../components/common/Pagination';
import EditOvertimeSlot from '../components/scheduling/EditOvertimeSlot';

class OvertimeSlots extends Component {
  state = {
    projectsList: ['all'],
    overtimeSlotsList: [],
    projectId: [],
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day'),
    limit: PAGINATION_LIMIT,
    page: 1,
    numberOfDaysSelected: 1,
    daysLimit: 31,
    isLoading: false,
    showEdit: false,
    showEditIndex: null,
  };

  pageHeading = {
    preTitle: 'Schedules',
    title: 'Overtime Slots',
  };

  projectSelectInputRef = React.createRef();

  componentDidMount() {
    this.getProjects();
    this.getOvertimeSlots();
  }

  handleReFetchEvents = () => {
    this.getOvertimeSlots();
  };

  handleShowEdit = (status, id) => {
    this.setState((state) => ({
      ...state,
      showEdit: status,
      showEditIndex: status ? id : null,
    }));
  };

  handleUpdateSlot = (formData) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      slotCount: formData.slotCount,
      otStart: formData.otStart,
      otEnd: formData.otEnd,
      slotId: formData.slotId,
    };

    schedulesService
      .update_overtime_slot(params)
      .then((result) => {
        if (result && result.status) {
          this.getOvertimeSlots();
          this.setState((state) => ({
            ...state,
            isLoading: false,
          }));
          this.handleShowEdit(false);
          // show success message
          toast.success('Overtime slot updated successfully');
        } else {
          toast.error(
            (result && result.data && result.data.message) ||
              'Something went wrong, please try again later.'
          );
        }
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
      })
      .finally(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleDeleteSlot = (slotId) => {
    const { overtimeSlotsList, page } = this.state;
    const slotsOnPage = overtimeSlotsList.reduce(
      (count, project) => count + project.slots.length,
      0
    );
    // Step back a page when the last row on it is removed
    const nextPage = slotsOnPage === 1 && page > 1 ? page - 1 : page;

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    schedulesService
      .delete_overtime_slot({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        slotId,
      })
      .then((result) => {
        if (result && result.status) {
          toast.success(OVERTIME_SLOTS.DELETE_SUCCESS);
          this.setState({ page: nextPage });
          // The refetch owns the loader from here
          this.getOvertimeSlots({ page: nextPage });
          return;
        }
        // makeRequest returns false after toasting the server message itself
        if (result) {
          toast.error(
            (result.data && result.data.message) ||
              'Something went wrong, please try again later.'
          );
        }
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      })
      .catch((error) => {
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getOvertimeSlots = (param) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { startDate, endDate, page, limit, projectId } = this.state;
    const dataFromDate = moment(startDate).format('YYYY-MM-DD');
    const dataToDate = moment(endDate).format('YYYY-MM-DD');

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      startDate: dataFromDate.concat(' 00:00:00'),
      endDate: dataToDate.concat(' 23:59:59'),
      page: (param && param.page) || page,
      projectId: (param && param.projectId) || projectId,
      limit,
    };

    if (
      (params && params.projectId && params.projectId.length === 0) ||
      (params && params.projectId && params.projectId.includes('all'))
    ) {
      delete params.projectId;
    }

    schedulesService
      .get_overtime_slots(params)
      .then((result) => {
        const overtimeSlotsFinal =
          (result && result.data && result.data.data) || [];
        const metaData = (result && result.data && result.data.metadata) || [];

        const groupedProjects = Object.values(
          overtimeSlotsFinal.reduce((acc, item) => {
            const { projectName, ...slot } = item;
            if (!acc[projectName]) {
              acc[projectName] = {
                projectName,
                slots: [],
              };
            }
            acc[projectName].slots.push(slot);
            return acc;
          }, {})
        );

        this.setState((state) => ({
          ...state,
          overtimeSlotsList: groupedProjects,
          overtimeSlotMetaData: metaData,
          isLoading: false,
        }));
      })
      .catch((error) => {
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getOvertimeSlotsExcel = (param) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const { startDate, endDate, projectId } = this.state;
    const dataFromDate = moment(startDate).format('YYYY-MM-DD');
    const dataToDate = moment(endDate).format('YYYY-MM-DD');

    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      startDate: dataFromDate.concat(' 00:00:00'),
      endDate: dataToDate.concat(' 23:59:59'),
      page: 1,
      projectId: (param && param.projectId) || projectId,
    };

    if (
      (params && params.projectId && params.projectId.length === 0) ||
      (params && params.projectId && params.projectId.includes('all'))
    ) {
      delete params.projectId;
    }

    schedulesService
      .get_overtime_slots(params)
      .then((result) => {
        const overtimeSlotsFinal =
          (result && result.data && result.data.data) || [];
        const flattenedOT = overtimeSlotsFinal;
        this.exportToExcel([...flattenedOT]);
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  exportToExcel = (param) => {
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    // Header row
    const header = [
      'Project Name',
      'Date',
      'Start Time',
      'End time',
      'Total Slots',
      'Used Slots',
      'Available Slots',
      'OT Fill Rate',
    ];

    const wsData = [
      header,
      ...param.map((slot) => [
        slot.projectName || '',
        moment(slot.otStartTz).format('MMMM D, YYYY') || '',
        formatTimeOption(slot.otStart)?.label || slot.otStart || '',
        formatTimeOption(slot.otEnd)?.label || slot.otEnd || '',
        slot.slotCount || 0,
        slot.slotUsed || 0,
        slot.slotCount > slot.slotUsed ? slot.slotCount - slot.slotUsed : 0,
        `${((slot.slotUsed / slot.slotCount) * 100).toFixed(0)}%` || '0%',
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);

    // Style the first row (header)
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4F81BD' } }, // Blue background
      alignment: { horizontal: 'center', vertical: 'center' },
    };

    header.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex }); // r = 0 for first row
      if (!worksheet[cellRef]) return;
      worksheet[cellRef].s = headerStyle;
    });

    // Optional: set column width or wrap text for time slots
    worksheet['!cols'] = [
      { wch: 20 }, // Name
      { wch: 15 }, // Employee Id
      { wch: 20 }, // Project name
      { wch: 15 }, // Date
      { wch: 30 }, // Time Slots
      { wch: 20 }, // Count
      { wch: 20 }, // Hours
      { wch: 15 }, // Type
    ];

    // Enable wrap text for Time Slots column (index 4)
    // eslint-disable-next-line no-plusplus
    for (let r = 1; r < wsData.length; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: 4 }); // Column 4 = "Time Slots"
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          alignment: { wrapText: true },
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'OT slots');

    const currentDate = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Overtime_slots_${currentDate}.xlsx`);
    this.setState((state) => ({
      ...state,
      isLoading: false,
    }));
  };

  getProjects = () => {
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
      filter: JSON.stringify({
        projectStatus: 'active',
      }),
    };
    // Fetch members and projects list
    projectService
      .get_projects_data(params)
      .then((result) => {
        const { projects } = result.data;
        this.setState((state) => ({
          ...state,
          projectsList: [...projects],
        }));
      })
      .catch((error) => {
        // show error message
        toast.error(
          (error && error.data) ||
            'Something went wrong, please try again later.'
        );
      });
  };

  handleDateSelect = (event, picker) => {
    picker.element.val(
      `${moment(picker.startDate).format('MMM D, YYYY')} - ${moment(
        picker.endDate
      ).format('MMM D, YYYY')}`
    );
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate),
      endDate: moment(picker.endDate),
    }));
  };

  handleProjectChange = (selectedProjects) => {
    this.setState({ projectId: selectedProjects });
  };

  render() {
    const {
      projectsList,
      startDate,
      endDate,
      isLoading,
      overtimeSlotsList,
      page,
      showEdit,
      showEditIndex,
      overtimeSlotMetaData,
    } = this.state;
    const { permissions } = this.props;
    const canManageSlots =
      permissions && permissions.includes('schedule_import');
    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            projectId: [],
            startDate,
            endDate,
            page: 1,
            limit: PAGINATION_LIMIT,
          }}
          onSubmit={(values) => {
            this.setState({
              page: 1,
            });
            this.getOvertimeSlots(values);
          }}
          innerRef={this.shrinkageFormRef}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit} className="mb-3">
              <Row>
                <Col>
                  <Row>
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="projectSelectInputRef"
                        name="projectId"
                        labelText="Projects"
                        fromArray={projectsList.map((project) => ({
                          value: project._id,
                          label: project.title,
                        }))}
                        formProps={formikprops}
                        isDisabled={projectsList.length < 1 && true}
                        innerRef={this.projectSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleProjectChange}
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <Form.Group>
                        <Form.Label>Date range</Form.Label>
                        <DateRangePicker
                          onEvent={this.handleDateSelect}
                          initialSettings={{
                            autoUpdateInput: false,
                            singleDatePicker: false,
                            startDate: moment().toDate(),
                            locale: {
                              format: 'MMM D, YYYY',
                            },
                            alwaysShowCalendars: true,
                            autoApply: true,
                            maxSpan: {
                              days: 30,
                            },
                          }}
                        >
                          <input
                            type="text"
                            className="form-control icon-calendar"
                            placeholder={`${moment().format(
                              'MMM D, YYYY'
                            )} - ${moment().format('MMM D, YYYY')}`}
                          />
                        </DateRangePicker>
                      </Form.Group>
                    </Col>
                    <Col md={6} lg={4}>
                      <Form.Group controlId="TeamMembers">
                        <Form.Label className="w-100">&nbsp;</Form.Label>
                        <Button variant="primary" type="Submit">
                          Generate
                        </Button>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-2 mb-4 justify-content-end">
                    <Col className="col-auto">
                      <ImportOvertime
                        handleReFetchEvents={this.handleReFetchEvents}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>

        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Row className="align-items-center my-3">
                  <Col xs="auto">
                    <b>Overtime Slots</b>
                  </Col>
                  {overtimeSlotsList && overtimeSlotsList.length > 0 && (
                    <Col xs="auto" className="ms-auto">
                      Download as{' '}
                      <Button
                        variant="white"
                        type="button"
                        size="sm"
                        className="mx-1"
                        onClick={() => this.getOvertimeSlotsExcel()}
                      >
                        Excel
                      </Button>
                    </Col>
                  )}
                </Row>
              </Card.Header>
              {overtimeSlotsList && overtimeSlotsList.length > 0 && (
                <div className="table-responsive">
                  <table
                    id="groupedDataByProject"
                    className="table table-sm table-hover card-table"
                  >
                    <thead className="thead-light">
                      <tr>
                        <th>Date</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Total Slots</th>
                        <th>Used Slots</th>
                        <th>Available Slots</th>
                        <th>OT Fill Rate</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overtimeSlotsList.map((item, index) => (
                        <React.Fragment key={index}>
                          <tr className="table-light" key={item.projectId}>
                            <td colSpan="10" className="px-4 py-2">
                              <b>{item.projectName}</b>
                            </td>
                          </tr>
                          {item.slots.map((slot, ind) => (
                            <tr key={ind}>
                              <td className="py-3">
                                {moment(slot.otStartTz).format('MMMM D, YYYY')}
                              </td>
                              <td className="py-3">
                                {formatTimeOption(slot.otStart)?.label ||
                                  slot.otStart ||
                                  ''}
                              </td>
                              <td className="py-3">
                                {formatTimeOption(slot.otEnd)?.label ||
                                  slot.otEnd ||
                                  ''}
                              </td>
                              <td className="py-3">{slot.slotCount}</td>
                              <td className="py-3">{slot.slotUsed}</td>
                              <td className="py-3">
                                {slot.slotCount > slot.slotUsed
                                  ? slot.slotCount - slot.slotUsed
                                  : 0}
                              </td>
                              <td className="py-3">
                                {`${(
                                  (slot.slotUsed / slot.slotCount) *
                                  100
                                ).toFixed(0)}%`}
                              </td>
                              <td className="py-3">
                                {canManageSlots && (
                                  <div className="d-flex gap-2">
                                    <EditOvertimeSlot
                                      showEdit={
                                        showEditIndex === slot._id && showEdit
                                      }
                                      handleShowEdit={this.handleShowEdit}
                                      slotDetail={slot}
                                      handleUpdateSlot={(param) => {
                                        this.handleUpdateSlot(param);
                                      }}
                                    />
                                    <DeleteOvertimeSlot
                                      slotDetail={slot}
                                      handleDeleteSlot={this.handleDeleteSlot}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>

                    {overtimeSlotMetaData &&
                      overtimeSlotMetaData.total > PAGINATION_LIMIT && (
                        <tfoot>
                          <tr>
                            <td colSpan="9">
                              <section className="d-flex justify-content-center py-3">
                                <Pagination
                                  className="pagination-bar"
                                  currentPage={page}
                                  totalCount={overtimeSlotMetaData.total}
                                  pageSize={PAGINATION_LIMIT}
                                  onPageChange={(pageN) => {
                                    this.setState({ page: pageN });
                                    this.getOvertimeSlots({
                                      page: pageN,
                                    });
                                  }}
                                />
                              </section>
                            </td>
                          </tr>
                        </tfoot>
                      )}
                  </table>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {!isLoading && overtimeSlotsList.length === 0 && (
          <Alert variant="info" className="text-center">
            {NO_DATA.OVERTIMESLOT}
          </Alert>
        )}
        {isLoading && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { permissionsReducer } = state;
  return {
    permissions: permissionsReducer,
  };
};

OvertimeSlots.propTypes = {
  permissions: PropTypes.array,
};

export default connect(mapStateToProps)(OvertimeSlots);
